/**
 * API-outage gate.
 *
 * Owns a proactive `/version/` heartbeat (so an outage is caught even when the
 * app is idle, not only when a request happens to fail) plus the recovery loop:
 * while the backend is up it polls `/version/` on a slow interval, and the
 * moment a probe fails it flips to a full-screen "reconnecting" hold screen,
 * polls faster, and does a full page reload once the backend answers again — so
 * the app comes back cleanly (and picks up any new bundle) after a tenant
 * redeploy rather than limping along with failed requests. The axios
 * interceptor (see monitor.reportApiFailure) is the fast path; this heartbeat
 * is the idle backstop.
 *
 * Contains:
 * - ApiHealthGate — wraps the app; renders children unless the API is down
 */
import { Box, CircularProgress, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { probeApiVersion } from '@sophocles/features/apiHealth/monitor';
import { selectApiDown, setApiDown } from '@sophocles/features/apiHealth/slice';

// Slow heartbeat while healthy; fast poll while reconnecting.
const HEARTBEAT_INTERVAL_MS = 15000;
const RECOVERY_POLL_MS = 3000;

export default function ApiHealthGate({ children }) {
  const isDown = useSelector(selectApiDown);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    let timer;

    const schedule = ms => {
      timer = setTimeout(tick, ms);
    };

    async function tick() {
      // Don't probe a backgrounded tab, but keep the schedule alive so it
      // resumes when the user returns.
      if (!isDown && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        schedule(HEARTBEAT_INTERVAL_MS);
        return;
      }

      const ok = await probeApiVersion();
      if (cancelled) return;

      if (isDown) {
        if (ok) {
          // Full reload rather than clearing the flag: re-fetches the bundle and
          // re-initializes from a clean slate after the backend has restarted.
          window.location.reload();
          return;
        }
        schedule(RECOVERY_POLL_MS);
      } else if (!ok) {
        // Healthy heartbeat failed → the backend just went down. Flip the flag;
        // the effect re-runs in recovery mode.
        dispatch(setApiDown());
      } else {
        schedule(HEARTBEAT_INTERVAL_MS);
      }
    }

    schedule(isDown ? RECOVERY_POLL_MS : HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isDown, dispatch]);

  if (!isDown) return children;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme => theme.zIndex.modal + 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 3,
        textAlign: 'center',
        bgcolor: 'var(--surface-page, background.default)',
        color: 'text.primary',
      }}
    >
      <CircularProgress />
      <Typography variant="h6">{t('apiHealth.reconnectingTitle', 'Reconnecting…')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
        {t(
          'apiHealth.reconnectingBody',
          "Can't reach the server right now. This page will refresh automatically once it's back.",
        )}
      </Typography>
    </Box>
  );
}

ApiHealthGate.propTypes = {
  children: PropTypes.node,
};
